/**
 * PDF export for a locked Progress Note. Builds from the SAME scribe-draft
 * blob the viewer hydrates (`readScribeDraftPayload` + `hydrate*FromScribeDraft`)
 * and mirrors its section render, so the PDF can't drift from the on-screen
 * note. Non-blob documents keep the legacy `ccdaToPdf` path.
 */
import pdfMake from 'pdfmake/build/pdfmake'
import * as pdfFonts from 'pdfmake/build/vfs_fonts'
import type { Content, TDocumentDefinitions } from 'pdfmake/interfaces'
import type { DocumentReference } from '@medplum/fhirtypes'
import { encounterLanguageDisclosure } from '@behuman/core-contract'
import logo from '/logo.png'
import { urlToDataURL } from '@/pages/Patient/components/OrderTests/pdf/pdfHelpers'
import { logError } from '@/utils/errorUtils'
import type { SoapNoteDto } from '@/pages/Patient/components/ProgressNoteEdit/draftToSoap'
import {
  buildExamNarrativeRow,
  hydrateAllergiesFromScribeDraft,
  hydrateConditionsFromScribeDraft,
  hydrateExamNarrativeFromScribeDraft,
  hydrateFamilyHistoryFromScribeDraft,
  hydrateFollowUpsFromScribeDraft,
  hydrateMedicationsFromScribeDraft,
  hydrateRosFromScribeDraft,
  hydrateServiceRequestsFromScribeDraft,
  hydrateSummaryFromScribeDraft,
  hydrateVitalObservationsFromScribeDraft,
  splitHpiParagraphs,
} from '@/pages/Patient/components/ProgressNoteEdit/scribeDraftPayload'
import { buildVitalReadRows } from '@/pages/Patient/components/ProgressNoteEdit/ProgressNoteSections/vitalReadRows'
import {
  computeTimeTotals,
  getAttestedCandidate,
  type BillingSuggestion,
  type MedicationItem,
  type ServiceRequestItem,
} from '@/pages/Patient/components/ProgressNoteEdit/progressNoteEditSchema'
import { CPT_SYSTEM, LOINC_SYSTEM } from '@/pages/Patient/components/ProgressNoteEdit/fhirSystems'
import { ROUTE_OPTIONS } from '@/pages/Patient/components/ProgressNoteEdit/rxnormPrefill'
import {
  PHYSICAL_EXAM_HEADING,
  TELEHEALTH_LIMITATIONS_SUBHEADING,
  TELEHEALTH_LIMITATIONS_TEXT,
} from '@/pages/Patient/components/ProgressNoteEdit/progressNoteText'
import { AI_DISCLOSURE_HEADING, AI_DISCLOSURE_TEXT } from '@/model/middleware-types'

const vfs =
  (pdfFonts as Record<string, unknown>).vfs ||
  (pdfFonts as Record<string, Record<string, unknown>>).pdfMake?.vfs
if (vfs) {
  pdfMake.vfs = vfs as Record<string, string>
}

const DIM = '#7D7987'
const EM_DASH = '—'
const LOGO_WIDTH = 60

// Mirrors the consent strings in `SubjectiveSection.tsx` (module-local there).
const IDENTITY_VERIFIED_TEXT = 'Patient identity verified.'
const CONSENT_BASE_TEXT = 'Patient consented to telehealth visit.'

export type ProgressNotePdfMetadata = {
  title: string
  source: string
  type: string
  documentName: string
  provider: string
  /** Formatted `Encounter.period.start` — never `DocumentReference.date`. */
  encounterDate: string
  addedOn: string
  modifiedOn: string
  patientName: string
  patientDob: string
}

export type ProgressNotePdfInput = {
  metadata: ProgressNotePdfMetadata
  documentReference: DocumentReference
  soapNote: SoapNoteDto
  /** Drives the Consent sentence — same source as `SubjectiveSection` (`usePatient`). */
  identityVerified: boolean
  /** "Electronically signed by …" — omitted for an unsigned note (PD-1897). */
  signature?: string
  /** Signed addenda only — a draft is not part of the record. */
  addenda?: PdfAddendum[]
  /**
   * The note's resolved billing projection (Claim codes + the AI context from the
   * DocumentReference extension). Overrides the draft blob's copy for Level of
   * Service and face-to-face minutes: no billing edit ever rewrites the blob, so
   * reading it here made the exported PDF disagree with the note on screen after a
   * coder changed the E/M code. Omitted → the blob is used, as before.
   */
  billingSuggestion?: BillingSuggestion
}

export type PdfAddendum = {
  reason: string
  /** The addendum's own "Electronically signed by …" line (PD-1897). */
  signature: string
  content: string
}

/** Mirrors `inferVisitType` in `LockedProgressNoteSections.tsx`. */
function inferVisitType(documentReference: DocumentReference): string {
  const typeText = `${documentReference.type?.text || ''} ${documentReference.type?.coding
    ?.map((coding) => coding.display || '')
    .join(' ')}`.toLowerCase()
  return typeText.includes('new') ? 'Initial Visit' : 'Follow-up Visit'
}

function sectionHeading(text: string): Content {
  return { text, style: 'subheader', margin: [0, 12, 0, 6] }
}

/** Subsection heading with the viewer's secondary LOINC label beside it. */
function subsectionHeading(text: string, loinc?: string): Content {
  const parts: Content[] = [{ text, bold: true }]
  if (loinc) {
    parts.push({ text: `  LOINC ${loinc}`, color: DIM, fontSize: 8, bold: false })
  }
  return { text: parts, style: 'tableHeader', margin: [0, 6, 0, 4] }
}

function paragraph(text: string): Content {
  return { text, margin: [0, 0, 0, 6] }
}

function emptyState(text: string): Content {
  return { text, color: DIM, fontSize: 9, italics: true, margin: [0, 0, 0, 6] }
}

/** Shared table shape: optional bold `#f0f0f0` header row + lightHorizontalLines. */
function pdfTable(
  headers: string[] | null,
  rows: Content[][],
  widths: (string | number)[],
): Content {
  const body: Content[][] = []
  if (headers) {
    body.push(headers.map((h) => ({ text: h, bold: true, fillColor: '#f0f0f0' })))
  }
  body.push(...rows)
  return {
    table: { headerRows: headers ? 1 : 0, widths, body },
    layout: 'lightHorizontalLines',
    margin: [0, 2, 0, 10],
  }
}

const capitalize = (value: string | null | undefined): string =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : EM_DASH

const formatMinutes = (minutes: number): string =>
  `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`

/**
 * Total Time row: label left, minutes right. `strong` marks a total; `indent`
 * nests a line item under its subtotal.
 */
function minutesRow(label: string, minutes: number, strong = false, indent = false): Content[] {
  return [
    { text: label, bold: strong, margin: indent ? [12, 0, 0, 0] : undefined },
    { text: formatMinutes(minutes), bold: strong, alignment: 'right' },
  ]
}

// Mirrors `getOrderPillLabel` in `PlanSection.tsx`.
function orderIntentLabel(intent: ServiceRequestItem['intent']): string {
  if (intent === 'order') return 'Ready to Place'
  if (intent === 'plan') return 'Planned'
  return 'Suggested'
}

const ORDER_PRIORITY_LABELS: Record<string, string> = {
  routine: 'Routine',
  urgent: 'Urgent',
  asap: 'ASAP',
  stat: 'STAT',
}

const MED_STATUS_LABELS: Record<MedicationItem['status'], string> = {
  active: 'Active',
  completed: 'Completed',
  intended: 'Planned',
  stopped: 'Stopped',
  'on-hold': 'On Hold',
}

// Mirrors `splitOrderCodes` in `PlanSection.tsx` — one (system, code) pair
// lands in either the CPT or LOINC column; unknown systems default to CPT.
function splitOrderCodes(order: ServiceRequestItem): { cpt: string; loinc: string } {
  if (!order.code) return { cpt: '', loinc: '' }
  if (order.system === CPT_SYSTEM) return { cpt: order.code, loinc: '' }
  if (order.system === LOINC_SYSTEM) return { cpt: '', loinc: order.code }
  return { cpt: order.code, loinc: '' }
}

export async function progressNoteToPdf({
  metadata,
  documentReference,
  soapNote,
  identityVerified,
  signature,
  addenda = [],
  billingSuggestion,
}: ProgressNotePdfInput): Promise<void> {
  // Same hydration chain as `LockedProgressNoteSections` (blob path).
  const summary = hydrateSummaryFromScribeDraft(soapNote)
  const hpiParagraphs = splitHpiParagraphs(summary.prob)
  const ros = hydrateRosFromScribeDraft(soapNote)
  const vitals = hydrateVitalObservationsFromScribeDraft(soapNote)
  const examNarrative = hydrateExamNarrativeFromScribeDraft(soapNote)
  const examRows = buildExamNarrativeRow(examNarrative)
  const allergies = hydrateAllergiesFromScribeDraft(soapNote)
  const conditions = hydrateConditionsFromScribeDraft(soapNote)
  const familyHistory = hydrateFamilyHistoryFromScribeDraft(soapNote)
  const orders = hydrateServiceRequestsFromScribeDraft(soapNote)
  const medications = hydrateMedicationsFromScribeDraft(soapNote)
  const followUps = hydrateFollowUpsFromScribeDraft(soapNote)

  const mdm = summary.mdm
  const totalTime = soapNote.billingSuggestion?.totalTime
  const providerEntries = totalTime?.providerEntries ?? []
  const coordinatorEntries = totalTime?.careCoordinatorEntries ?? []
  const faceToFaceMinutes =
    billingSuggestion?.durationMinutes ?? soapNote.billingSuggestion?.durationMinutes ?? 0
  const { sameDayMinutes, coordinatorMinutes, providerTotal, combinedTotal } = computeTimeTotals(
    faceToFaceMinutes,
    providerEntries,
    coordinatorEntries,
  )

  // Best-effort: a logo fetch failure must never block an export (PD-1362).
  let logoDataUrl = ''
  try {
    logoDataUrl = await urlToDataURL(logo)
  } catch (err) {
    logError('patient', 'Progress note PDF logo', err)
  }

  const content: Content[] = []

  const title: Content = { text: metadata.title, style: 'header', alignment: 'center' }
  content.push(
    logoDataUrl
      ? {
          columns: [
            { width: LOGO_WIDTH, text: '' },
            { ...title, width: '*' },
            { image: logoDataUrl, width: LOGO_WIDTH, margin: [0, 4, 0, 0] },
          ],
          margin: [0, 0, 0, 10],
        }
      : { ...title, margin: [0, 0, 0, 10] },
  )

  // --- Document Information — mirrors `<NoteMetadataGrid>` ---
  content.push(
    { text: 'Document Information', style: 'subheader', margin: [0, 0, 0, 8] },
    {
      columns: [
        {
          width: '50%',
          stack: [
            { text: [{ text: 'Patient: ', bold: true }, metadata.patientName] },
            { text: [{ text: 'Source: ', bold: true }, metadata.source] },
            { text: [{ text: 'Type: ', bold: true }, metadata.type] },
            { text: [{ text: 'Document Name: ', bold: true }, metadata.documentName] },
            { text: [{ text: 'Provider: ', bold: true }, metadata.provider] },
          ],
        },
        {
          width: '50%',
          stack: [
            { text: [{ text: 'Date of Birth: ', bold: true }, metadata.patientDob] },
            { text: [{ text: 'Added on: ', bold: true }, metadata.addedOn] },
            { text: [{ text: 'Modified on: ', bold: true }, metadata.modifiedOn] },
            { text: [{ text: 'Encounter Date: ', bold: true }, metadata.encounterDate] },
          ],
        },
      ],
      margin: [0, 0, 0, 4],
    },
  )

  // --- 1. Encounter Details ---
  content.push(sectionHeading('Encounter Details'))
  content.push(
    pdfTable(
      ['Date', 'Type', 'Description'],
      [
        [
          metadata.encounterDate || EM_DASH,
          inferVisitType(documentReference),
          // The note's Chief Complaint, as on screen — never the stored description (PD-1889).
          summary.examReason.trim() || '-',
        ],
      ],
      [110, 90, '*'],
    ),
  )

  // --- 2. Subjective ---
  content.push(sectionHeading('Subjective'))
  content.push(subsectionHeading('Consent'))
  content.push(
    paragraph(
      identityVerified ? `${IDENTITY_VERIFIED_TEXT} ${CONSENT_BASE_TEXT}` : CONSENT_BASE_TEXT,
    ),
  )
  if (summary.examReason.trim()) {
    content.push(subsectionHeading('Chief Complaint'))
    content.push(paragraph(summary.examReason))
  }
  if (hpiParagraphs.length > 0) {
    content.push(subsectionHeading('History of Present Illness (HPI)', '10164-2'))
    for (const para of hpiParagraphs) {
      content.push(paragraph(para.text))
    }
  }
  if (ros.length > 0) {
    content.push(subsectionHeading('Review of Systems'))
    content.push(
      pdfTable(
        ['System', 'Finding'],
        ros.map((row) => [{ text: row.system || EM_DASH, bold: true }, row.finding || EM_DASH]),
        [140, '*'],
      ),
    )
  }

  // --- 3. Objective ---
  content.push(sectionHeading('Objective'))
  content.push(subsectionHeading('Vitals'))
  if (vitals.length === 0) {
    content.push(emptyState('No vitals captured.'))
  } else {
    content.push(
      pdfTable(
        null,
        buildVitalReadRows(vitals).map((row) => [
          { text: row.label, bold: true },
          row.note
            ? {
                stack: [
                  { text: row.value },
                  { text: row.note, color: DIM, fontSize: 8, italics: true },
                ],
              }
            : { text: row.value },
        ]),
        [140, '*'],
      ),
    )
  }
  content.push(subsectionHeading(PHYSICAL_EXAM_HEADING))
  content.push({
    text: TELEHEALTH_LIMITATIONS_SUBHEADING,
    bold: true,
    fontSize: 9,
    margin: [0, 2, 0, 3],
  })
  // Single-cell borderless table = the viewer's light boxed paragraph;
  // pdfmake handles page-breaks inside tables.
  content.push({
    table: {
      widths: ['*'],
      body: [[{ text: TELEHEALTH_LIMITATIONS_TEXT, fillColor: '#f9fafb', margin: [4, 4, 4, 4] }]],
    },
    layout: 'noBorders',
    margin: [0, 0, 0, 6],
  })
  if (examRows.length > 0) {
    content.push(paragraph(examNarrative))
  }
  if (allergies.length > 0) {
    content.push(subsectionHeading('Allergies', '48765-2'))
    content.push(
      pdfTable(
        ['Allergy', 'Reaction', 'Category', 'Status'],
        allergies.map((a) => [
          {
            text: `${a.display || a.code || EM_DASH}${a.external ? ' (Chart)' : ''}`,
          },
          a.reaction || EM_DASH,
          capitalize(a.category),
          capitalize(a.clinicalStatus),
        ]),
        ['*', '*', 80, 70],
      ),
    )
    // Same footer as the viewer: the first SNOMED-coded allergy's code.
    const firstSnomedCode = allergies.find((a) => {
      if (!a.code) return false
      if (!a.system) return true
      return a.system.toLowerCase().includes('snomed')
    })?.code
    if (firstSnomedCode) {
      content.push({
        text: `SNOMED ${firstSnomedCode}`,
        color: DIM,
        fontSize: 8,
        margin: [0, -6, 0, 8],
      })
    }
  }

  // --- 4. Assessment ---
  content.push(sectionHeading('Assessment'))
  if (summary.assessment.trim()) {
    content.push(subsectionHeading('Clinical Impression'))
    content.push(paragraph(summary.assessment))
  }
  content.push(subsectionHeading('Problem List', '11450-4'))
  const encounterDx = conditions.filter((c) => c.category === 'encounter-diagnosis')
  if (encounterDx.length === 0) {
    content.push(emptyState('No diagnoses captured.'))
  } else {
    content.push(
      pdfTable(
        ['ICD-10', 'Diagnosis', 'Status'],
        encounterDx.map((c) => [
          { text: c.code || EM_DASH, bold: true },
          c.display || EM_DASH,
          capitalize(c.clinicalStatus),
        ]),
        [70, '*', 80],
      ),
    )
  }
  // Hide-when-empty matches the viewer's PMH rule (PD-1177) — no emptyState.
  const pmhRows = conditions.filter((c) => c.category === 'problem-list-item')
  if (pmhRows.length > 0) {
    content.push(subsectionHeading('Past Medical History'))
    content.push(
      pdfTable(
        ['ICD-10', 'Diagnosis', 'Status'],
        pmhRows.map((c) => [
          { text: c.code || EM_DASH, bold: true },
          c.display || EM_DASH,
          capitalize(c.clinicalStatus),
        ]),
        [70, '*', 80],
      ),
    )
  }
  // Hide-when-empty, mirroring the viewer (PD-1377).
  if (familyHistory.length > 0) {
    content.push(subsectionHeading('Family History'))
    content.push(
      pdfTable(
        ['Relative', 'Conditions'],
        familyHistory.map((row) => [
          { text: row.deceased ? `${row.relative} (deceased)` : row.relative, bold: true },
          row.conditions.join(', ') || EM_DASH,
        ]),
        [140, '*'],
      ),
    )
  }

  // --- 5. Plan (billing grid intentionally excluded) ---
  content.push(sectionHeading('Plan'))
  if (summary.plan.trim()) {
    content.push(subsectionHeading('Plan Narrative'))
    content.push(paragraph(summary.plan))
  }
  if (orders.length > 0) {
    content.push(subsectionHeading('Orders / Service Requests', '47519-4'))
    content.push(
      pdfTable(
        ['Order', 'CPT', 'LOINC', 'Intent', 'Priority'],
        orders.map((order) => {
          const { cpt, loinc } = splitOrderCodes(order)
          return [
            order.display || EM_DASH,
            cpt || EM_DASH,
            loinc || EM_DASH,
            orderIntentLabel(order.intent),
            ORDER_PRIORITY_LABELS[order.priority ?? 'routine'] || capitalize(order.priority),
          ]
        }),
        ['*', 55, 55, 75, 55],
      ),
    )
  }
  if (medications.length > 0) {
    content.push(subsectionHeading('Medications', '10160-0'))
    content.push(
      pdfTable(
        ['Medication', 'Dose', 'Route', 'Frequency', 'Status', 'Note'],
        medications.map((m) => [
          m.code
            ? {
                stack: [
                  { text: m.display || m.code, bold: true },
                  { text: `RxNorm ${m.code}`, color: DIM, fontSize: 8 },
                ],
              }
            : { text: m.display || EM_DASH, bold: true },
          m.dose || EM_DASH,
          m.route ? (ROUTE_OPTIONS.find((o) => o.value === m.route)?.label ?? m.route) : EM_DASH,
          m.frequency || EM_DASH,
          MED_STATUS_LABELS[m.status] || capitalize(m.status),
          { text: m.note || EM_DASH, color: DIM, fontSize: 9 },
        ]),
        ['*', 50, 70, 60, 55, 90],
      ),
    )
  }
  // --- 6. Medical Decision Making · 7. Total Time · 8. Follow-Up ---
  // Total Time renders even though the billing grid above stays excluded: the
  // minutes substantiate the E/M level, the candidate chips don't belong here.
  if (mdm.trim()) {
    content.push(sectionHeading('Medical Decision Making'))
    content.push(paragraph(mdm))
    const attested = getAttestedCandidate(billingSuggestion ?? soapNote.billingSuggestion)
    if (attested) {
      content.push({
        text: [
          { text: 'Level of service: ', bold: true },
          `${attested.code}${attested.display ? ` — ${attested.display}` : ''}`,
        ],
        margin: [0, 0, 0, 6],
      })
    }
  }

  // Coordinator-only time must still render the section (mirrors the viewer).
  if (providerTotal > 0 || coordinatorEntries.length > 0) {
    content.push(sectionHeading('Total Time'))
    if (providerTotal > 0) {
      content.push(
        pdfTable(
          null,
          [
            ...(faceToFaceMinutes > 0
              ? [minutesRow('Face-to-face telehealth visit', faceToFaceMinutes)]
              : []),
            ...(sameDayMinutes > 0 ? [minutesRow('Same-day provider work', sameDayMinutes)] : []),
            ...providerEntries.map((e) => minutesRow(e.label, e.minutes, false, true)),
            minutesRow('Provider total', providerTotal, true),
          ],
          ['*', 90],
        ),
      )
    }
    if (coordinatorEntries.length > 0) {
      content.push(subsectionHeading('Care-Coordinator Time'))
      content.push(
        pdfTable(
          null,
          [
            ...coordinatorEntries.map((e) => minutesRow(e.label, e.minutes)),
            minutesRow('Care-coordinator total', coordinatorMinutes, true),
          ],
          ['*', 90],
        ),
      )
      content.push({
        text: `Combined team workload: ${formatMinutes(combinedTotal)}. The combined workload is shown for operational tracking and is not the provider's E/M billing time.`,
        color: DIM,
        fontSize: 8,
        margin: [0, -4, 0, 8],
      })
    }
  }

  if (followUps.length > 0) {
    content.push(sectionHeading('Follow-Up'))
    content.push({
      ul: followUps.map((f) => f.text),
      margin: [0, 0, 0, 10],
    })
  }

  if (soapNote.aiAssisted) {
    content.push(sectionHeading(AI_DISCLOSURE_HEADING))
    content.push(
      paragraph(
        [encounterLanguageDisclosure(soapNote.encounterLanguage), AI_DISCLOSURE_TEXT]
          .filter(Boolean)
          .join(' '),
      ),
    )
  }

  if (signature) {
    content.push(sectionHeading('Electronic Signature'))
    content.push(paragraph(signature))
  }

  // Addenda close the document — the original note must read in full first.
  if (addenda.length > 0) {
    content.push(sectionHeading('Addenda'))
    addenda.forEach((addendum) => {
      content.push(subsectionHeading(addendum.reason))
      content.push({
        text: addendum.signature,
        color: DIM,
        fontSize: 8,
        margin: [0, -4, 0, 4],
      })
      content.push(paragraph(addendum.content))
    })
  }

  const docDefinition: TDocumentDefinitions = {
    pageSize: 'A4',
    content,
    styles: {
      header: { fontSize: 18, bold: true },
      subheader: { fontSize: 14, bold: true },
      tableHeader: { fontSize: 12, bold: true },
    },
    defaultStyle: { fontSize: 10 },
  }

  const fileName = `${metadata.title.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`
  pdfMake.createPdf(docDefinition).getBlob((blob) => {
    const url = URL.createObjectURL(blob)
    window.open(url, '_blank')
    const a = document.createElement('a')
    a.href = url
    a.download = fileName
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  })
}
